import { useGetCentralBranding, useGetSettingsBasicPublic } from '@agos/api-client/platform'
import { useGetSalesChannels } from '@agos/api-client/orders'
import {
  getGetNotificationsQueryKey,
  getGetNotificationsUnreadCountQueryKey,
  useGetNotifications,
  useGetNotificationsUnreadCount,
  usePostNotificationsIdRead,
  usePostNotificationsReadAll,
} from '@agos/api-client/notifications'
import {
  filterChannelsOf,
  restaurantNameOf,
  courierRedirect,
  getCurrentLanguage,
  isExternalOf,
  langInfo,
  LANGUAGE_TO_COUNTRY,
  MenuPreviewProvider,
  offeredLanguages,
  setLanguage,
  T,
  ThemeToggle,
  tt,
  useAuth,
  useIsMobile,
  useTheme,
  useUnsavedWorkGate,
  type FilterChannel,
} from '@agos/shared'
import { ConfirmDialog, Spinner, cn } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Bell, Check, ChevronDown, ListChecks, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Receipt, Truck, User, Utensils, Wine, X } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'wouter'

import { NAV_SECTIONS, type NavGuard } from '../generated/nav-tree'
import type { RouteGuard } from '../module-contract'
import { SidebarAttendance } from '@agos/manager-hr'
import { useUnacceptedCount } from '@agos/manager-delivery'
import { AGOS_NAV_SECTIONS } from './agos-nav'
import { withChannelBoards } from './channel-boards-nav'
import type { LucideIcon } from 'lucide-react'
import { FranchiseSwitcher } from './FranchiseSwitcher'
import { canAccess, useAccessContext, type AccessContext } from './access'
import { headerSurfacesFor } from './header-surfaces'
import { navEntryVisible, navItemVisible, routeGuardsByPath } from './nav-visibility'
import { badgeText, isUnread, maySeeNotifications, unreadOf } from './notification-bell'
import { menuPreviewOf } from './menu-preview'
import { NavCountBadge, UnacceptedOrdersLink } from './UnacceptedOrders'
import { signedInRoleName } from './user-pill'
import {
  agoLabel,
  linkFor,
  notificationCategory,
  notificationSubject,
  pageOf,
  type Severity,
} from '@agos/manager-settings'
import { composedRoutes } from '../module-registry'

/**
 * Every route the router serves, and what it will actually enforce, by path - §9.224.
 *
 * Built once: `composedRoutes()` reads static module arrays, so this cannot change between renders,
 * and rebuilding a 57-entry map on every sidebar render would be work for nothing. The list itself is
 * kept for §9.759's header links, which must tell an unguarded route from one that does not exist.
 */
const ROUTES = composedRoutes()
const ROUTE_GUARDS = routeGuardsByPath(ROUTES)

/** *Rozvoz - nástenka*'s path - the entry that carries the count of unaccepted orders, §9.674. */
const DISPATCH_PATH = '/dispatch'

/**
 * The chrome, ported from legacy `components/layout.tsx`: a dark sidebar of
 * nine sections, a header carrying the franchise switcher and the user menu,
 * and the page in the middle.
 *
 * The sidebar is a drawer below 768 px because kitchen and POS run on tablets
 * (§14.9). Collapsible sections keep their open state per section key, as
 * legacy does, so a dispatcher who lives in one section is not re-opening it
 * every shift.
 */

/**
 * A section the sidebar can draw — legacy's, or one of §9.30's.
 *
 * The two differ only in the guard each item carries: legacy's are `NavGuard` (permission and tier),
 * the AGOS pages' are `RouteGuard` (which adds `systemRoles`). `canAccess` has taken either since
 * §9.7 gave the sidebar and the router one evaluator, so widening the prop is all that is needed —
 * and the generated file stays generated.
 */
interface DrawableSection {
  heading: string
  sectionKey?: string | null
  collapsible?: boolean
  guard?: NavGuard | RouteGuard | undefined
  items: ReadonlyArray<{
    label: string
    path: string
    icon: LucideIcon | null
    guard: NavGuard | RouteGuard | undefined
  }>
}

/**
 * An AGOS section that legacy has no counterpart for, so it draws on its own — §9.112.
 *
 * <p>A section whose heading matches one of legacy's is **merged into it** instead. Drawing a second
 * block called *Správa rozvoz* under the first would read as a bug; the owner asked for the driver app
 * to sit *"na lištu k správe rozvozu"*, which is that section and not one beside it.
 */
function isItsOwnSection(section: { heading: string }): boolean {
  return !NAV_SECTIONS.some((generated) => generated.heading === section.heading)
}

/** Legacy's section, plus any AGOS items that belong in it. */
function withAgosExtras(section: DrawableSection): DrawableSection {
  const extras = AGOS_NAV_SECTIONS.filter((agos) => agos.heading === section.heading).flatMap(
    (agos) => agos.items,
  )
  return extras.length === 0 ? section : { ...section, items: [...section.items, ...extras] }
}

/** §9.688 - and a channel's own Kuchyňa and Otvorené stoly, under the ordinary ones they split from. */
function withChannelBoardsIn(
  section: DrawableSection,
  channels: readonly FilterChannel[],
): DrawableSection {
  return { ...section, items: withChannelBoards(section.items, channels) }
}

/**
 * Whether the sidebar was folded away last time - §9.368.
 *
 * **Wrapped, because a private window throws on access rather than returning null**, and a shell
 * that failed to render because it could not read a preference would be a far worse bug than the one
 * this setting exists for. An unreadable store means *not collapsed*, which is the state the app has
 * always had.
 */
const COLLAPSED_KEY = 'agos.sidebar-collapsed'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0')
  } catch {
    // Nothing to do and nothing to say: the panel still folds, it just will not be remembered.
  }
}

function SidebarSection({
  section,
  access,
  onNavigate,
  badgeFor,
}: {
  section: DrawableSection
  access: AccessContext
  /**
   * §9.631, FB-944 - the event and the target, because the layout may need to refuse the navigation.
   *
   * This took no arguments and closed the drawer. A guard has to be able to `preventDefault` and then
   * navigate itself once the question is answered, which needs both.
   */
  onNavigate: (event: { preventDefault: () => void }, href: string, active: boolean) => void
  /**
   * §9.674, owner O58 - the badge text an entry carries, by path, or the empty string for none.
   *
   * A function rather than a map so the section does not have to know which entries are counted: today it is
   * *Rozvoz - nástenka*'s unaccepted orders and nothing else.
   */
  badgeFor?: (path: string) => string
}) {
  const [location] = useLocation()
  /**
   * Closed until opened — §9.7.1, and a knowing departure from legacy.
   *
   * Legacy opens all nine at once, and this file copied that on purpose. On a tablet, which is where
   * this app runs, that is a wall of links to scroll past before reaching anything. The open state is
   * still remembered per section, so the original reasoning — *a dispatcher is not re-opening their
   * section every shift* — survives; it is only the starting state that changes.
   *
   * The section holding the current page opens itself, or somebody who followed a link would find the
   * sidebar disagreeing with the screen.
   */
  const [collapsed, setCollapsed] = useState(
    () => !section.items.some((item) => item.path === window.location.pathname),
  )

  if (!canAccess(section.guard, access)) return null

  // §9.224 — the router's guard as well as the sidebar's own, so a link cannot be offered that
  // `RouteGate` would answer with the lock panel.
  const visibleItems = section.items.filter((item) => navItemVisible(item, ROUTE_GUARDS, access))
  // A section whose every item is gated away is not rendered as an empty
  // heading — legacy hides the whole block.
  if (visibleItems.length === 0) return null

  const isOpen = section.collapsible !== true || !collapsed

  return (
    <div className="mb-1">
      {section.collapsible === true ? (
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          // A control that folds something has to say whether it is folded. Without this the only
          // statement of the section's state was the chevron's rotation, which a screen reader cannot
          // read and a test cannot ask.
          aria-expanded={isOpen}
          data-nav-section={section.heading}
          className="w-full flex items-center justify-between px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-sidebar-foreground/40 hover:text-sidebar-foreground/70 transition-colors"
        >
          <T>{section.heading}</T>
          <ChevronDown className={cn('w-3 h-3 transition-transform', !isOpen && '-rotate-90')} />
        </button>
      ) : (
        <p className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-sidebar-foreground/40">
          <T>{section.heading}</T>
        </p>
      )}

      <div className={cn('collapsible-section', isOpen && 'open')}>
        <div>
          {visibleItems.map((item) => {
            const Icon = item.icon
            const isActive = location === item.path
            return (
              <Link
                key={item.path}
                href={item.path}
                onClick={(event) => onNavigate(event, item.path, isActive)}
                className={cn(
                  'flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-sidebar-primary/15 text-sidebar-primary font-semibold border-r-2 border-sidebar-primary'
                    : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/5',
                )}
              >
                {Icon && <Icon className="w-4 h-4 shrink-0" />}
                <span className="truncate">
                  <T>{item.label}</T>
                </span>
                {/* §9.674 - after the label, so the entry still reads as its name first. */}
                <NavCountBadge text={badgeFor?.(item.path) ?? ''} path={item.path} />
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * The bell, left of the language control - FRONTEND.md §9.348, the owner's C4.
 *
 * **A missed port rather than a new feature.** Legacy's `layout.tsx:231` is a `NotificationBell` and
 * its lines 582-583 render it immediately before the language switcher, which is exactly where the
 * owner asked for it: *"in the top right corner left to the language selector"*. AGOS ported the page
 * and the unread endpoint and left the bell behind.
 *
 * **Sixty seconds, as legacy polls.** The count is a badge somebody glances at, not a figure anything
 * is computed from, so a minute late is right and a socket would be a new dependency for it.
 *
 * No pulse animation, which legacy has: this sits beside a static language flag all day, and a
 * permanent animation next to it reads as a fault rather than as news.
 */
/** How many the panel previews. Enough to be worth opening, few enough not to need scrolling. */
const PANEL_ROWS = 6

const PANEL_DOT: Record<Severity, string> = {
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
}

function NotificationBell({
  askFirst,
}: {
  /**
   * §9.631, FB-944 - the layout's guard, passed down rather than a second one built here.
   *
   * This panel navigates in one tap, twice: a per-notification jump and *Zobraziť všetky*. Calling
   * `useUnsavedWorkGate` here instead would mean a second `ConfirmDialog` in the same layout, and two
   * questions about one thing is how an operator learns to dismiss both.
   */
  askFirst: (leave: () => void) => (event: { preventDefault: () => void }) => void
}) {
  const { user } = useAuth()
  /* §9.631 - the guard refuses the anchor's own navigation, so each of these two links performs it itself once
     the question is answered. Without this, confirming would close the panel and go nowhere. */
  const [, navigate] = useLocation()
  const maySee = maySeeNotifications(user?.systemRole)
  const [open, setOpen] = useState(false)
  const { data } = useGetNotificationsUnreadCount({
    query: { refetchInterval: 60_000, enabled: maySee },
  })
  /* §9.424 - the preview, fetched only while the panel is open.
   *
   * The owner: *"When clicking on the bell, there could be preview of some notifications, and when
   * clicking agaiin that /notifications."* The badge already polls every minute for every member of
   * staff all day; dragging a list along with it would be a second request a minute that nobody has
   * asked to see. `enabled` is the whole difference. */
  const preview = useGetNotifications(
    { limit: PANEL_ROWS, offset: 0 },
    { query: { enabled: maySee && open } },
  )

  /* §9.516, the owner: *"you can actually close them, mark as read"*. Both endpoints have existed since
   * the inbox was built and `NotificationsPage` already calls them; the panel - the thing anybody
   * actually opens - called neither, so the badge never came down and a count that never falls is one
   * people stop reading. */
  const queryClient = useQueryClient()
  const refreshBell = () => {
    void queryClient.invalidateQueries({ queryKey: getGetNotificationsUnreadCountQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getGetNotificationsQueryKey() })
  }
  const markOne = usePostNotificationsIdRead({ mutation: { onSuccess: refreshBell } })
  const markAll = usePostNotificationsReadAll({ mutation: { onSuccess: refreshBell } })
  if (!maySee) return null
  const badge = badgeText(unreadOf(data))
  const rows = pageOf(preview.data).notifications

  return (
    <div className="relative">
      <button
        type="button"
        data-notification-bell
        aria-label={tt('Oznámenia')}
        aria-expanded={open}
        title={tt('Oznámenia')}
        onClick={() => setOpen((value) => !value)}
        /* `z-50` so the bell sits **above its own backdrop** - §9.424.
         *
         * The backdrop that closes the panel on a click elsewhere is `fixed inset-0 z-40`, and the
         * bell was under it: clicking the bell again did nothing at all, because the backdrop took
         * the click. Which is precisely what the owner asked for - *"when clicking agaiin"*. Caught
         * by the e2e with `<div class="fixed inset-0 z-40"> intercepts pointer events`, not by
         * looking at it, because the panel still closed on a click *anywhere else* and the bug was
         * invisible unless you tried the bell twice. */
        className="relative z-50 flex items-center p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      >
        <Bell className="w-5 h-5" />
        {badge !== '' && (
          <span
            data-notification-badge
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center"
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* The same backdrop the language menu uses: a click anywhere else closes it. */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-notification-panel
            className="absolute right-0 top-full mt-1 z-50 w-[22rem] rounded-xl border border-border bg-card shadow-xl overflow-hidden"
          >
            <div className="px-3 py-2 border-b border-border flex items-center justify-between gap-2">
              <p className="text-sm font-bold">
                <T>Oznámenia</T>
              </p>
              {badge !== '' && (
                <span className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-destructive" data-panel-unread>
                    {badge} <T>neprečítané</T>
                  </span>
                  {/* §9.516 - the count and the way to clear it belong in the same place rather than one
                    * screen apart, which is where *mark all* used to live. */}
                  <button
                    type="button"
                    data-panel-mark-all
                    disabled={markAll.isPending}
                    onClick={() => markAll.mutate()}
                    className="text-[11px] font-semibold text-primary hover:underline disabled:opacity-50"
                  >
                    <T>Prečítať všetky</T>
                  </button>
                </span>
              )}
            </div>

            {preview.isLoading ? (
              <div className="flex justify-center py-8" data-panel-loading>
                <Spinner />
              </div>
            ) : preview.isError ? (
              <p className="px-3 py-6 text-center text-xs text-destructive" data-panel-error>
                <T>Oznámenia sa nepodarilo načítať.</T>
              </p>
            ) : rows.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-muted-foreground" data-panel-empty>
                <T>Žiadne oznámenia.</T>
              </p>
            ) : (
              <div className="max-h-80 overflow-y-auto divide-y divide-border">
                {rows.map((one) => {
                  const unread = isUnread(one)
                  return (
                    <div key={one.id} className="flex items-start hover:bg-muted/50 transition-colors">
                      <Link
                        href={linkFor(one) ?? '/notifications'}
                        data-panel-notification={one.id}
                        data-panel-unread-row={unread ? 'yes' : 'no'}
                        /* §9.516 - reading one marks it, and then follows the link. Both, not one or the
                         * other: the notice has been read, and the destination is the point of it.
                         *
                         * **Fired and not awaited on purpose.** The navigation must not wait on a write,
                         * and a failed mark is a badge that stays up for a minute - recoverable and
                         * silent - while a blocked navigation is the feature not working. */
                        onClick={askFirst(() => {
                          if (unread) markOne.mutate({ id: one.id })
                          setOpen(false)
                          navigate(linkFor(one) ?? '/notifications')
                        })}
                        className="flex items-start gap-2.5 px-3 py-2 min-w-0 flex-1"
                      >
                        <span
                          className={cn(
                            'mt-1.5 w-1.5 h-1.5 rounded-full shrink-0',
                            unread ? PANEL_DOT[one.severity] : 'bg-muted-foreground/30',
                          )}
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1">
                          {/* The item, not the category - the category repeats and is on the second line. */}
                          <span
                            className={cn(
                              'block text-xs truncate',
                              /* §9.516 - every row looked identical, so a panel of ten said nothing about
                               * which three were new. It is also what makes the dismiss legible: a control
                               * whose effect you cannot see reads as broken. */
                              unread ? 'font-semibold text-foreground' : 'text-muted-foreground',
                            )}
                          >
                            {notificationSubject(one.title)}
                          </span>
                          <span className="block text-[11px] text-muted-foreground truncate">
                            {notificationCategory(one.title) || one.body}
                          </span>
                        </span>
                        <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
                          {agoLabel(one.createdAt)}
                        </span>
                      </Link>
                      {unread && (
                        <button
                          type="button"
                          data-panel-dismiss={one.id}
                          aria-label={tt('Označiť ako prečítané')}
                          title={tt('Označiť ako prečítané')}
                          disabled={markOne.isPending}
                          /* Stops the row's click, or dismissing a notice would also navigate away from
                            * the panel it was dismissed in. */
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            markOne.mutate({ id: one.id })
                          }}
                          className="shrink-0 p-2 text-muted-foreground hover:text-foreground disabled:opacity-40"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            <Link
              href="/notifications"
              data-panel-all
              onClick={askFirst(() => {
                setOpen(false)
                navigate('/notifications')
              })}
              className="block px-3 py-2 border-t border-border text-center text-xs font-bold text-primary hover:bg-muted/50 transition-colors"
            >
              <T>Zobraziť všetky</T>
            </Link>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * The language the till is run in — §9.11.
 *
 * The catalogue, the runtime and the `fk_lang` key are `@agos/shared`'s, so this and the customer app
 * offer the same list and write the same preference: one browser, one choice, whether the person is
 * taking an order or placing one.
 *
 * **Only the locales with bundles.** Twelve languages are catalogued and seven ship translations;
 * offering the other five would switch the app into a language it then falls back to Slovak for, which
 * reads as the switcher being broken rather than as the translation being absent.
 */
function LanguageMenu() {
  // §9.92 — only the languages the franchise turned on in *AI preklad*. `basic-public` was built to
  // answer exactly this and nothing read it; `offeredLanguages` keeps the default and falls back to
  // every shipped bundle when the call has not answered, so a slow read never shrinks the menu.
  const { data: languageSettings } = useGetSettingsBasicPublic()
  const offered = offeredLanguages(
    (languageSettings as { autoTranslateLanguages?: string[] } | undefined)?.autoTranslateLanguages,
    (languageSettings as { defaultLanguage?: string } | undefined)?.defaultLanguage,
  )

  const [open, setOpen] = useState(false)
  const current = getCurrentLanguage()

  return (
    <div className="relative">
      <button
        type="button"
        data-language-open
        onClick={() => setOpen((value) => !value)}
        // *Zmeniť jazyk*, not *Jazyk*: `/settings/translations` has its own **Jazyk** select, and two
        // controls answering to one accessible name is ambiguous to a screen reader before it is
        // ambiguous to a test.
        aria-label={tt('Zmeniť jazyk')}
        // §9.424 - the same latent bug the bell had, found by fixing the bell: this trigger sat
        // under its own `fixed inset-0 z-40` backdrop, so clicking it a second time did nothing.
        // One menu away, identical shape, and nothing was asserting it.
        className="relative z-50 flex items-center gap-1.5 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      >
        <img
          src={`/flags/${LANGUAGE_TO_COUNTRY[current] ?? 'sk'}.svg`}
          alt=""
          className="w-5 h-auto rounded-[2px]"
        />
        <span className="text-xs font-bold uppercase">{current}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-language-menu
            className="absolute right-0 top-full mt-1 z-50 w-44 rounded-xl border border-border bg-card shadow-xl py-1"
          >
            {offered.map((code) => (
              <button
                key={code}
                type="button"
                data-language={code}
                onClick={() => {
                  setLanguage(code)
                  setOpen(false)
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors"
              >
                <img
                  src={`/flags/${LANGUAGE_TO_COUNTRY[code] ?? code}.svg`}
                  alt=""
                  className="w-5 h-auto rounded-[2px]"
                />
                <span className="flex-1 text-left">{langInfo(code).label}</span>
                {code === current && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * The three places the owner reaches for often enough to want them one press away — §9.11.
 *
 * Each carries the same guard as its own sidebar entry. A shortcut to a page somebody cannot open is
 * worse than no shortcut: it looks like a permission problem rather than a design one.
 */
const HEADER_SHORTCUTS = [
  {
    path: '/lists',
    label: tt('Zoznamy & Archív'),
    icon: ListChecks,
    // §9.31 — the hue follows the thing, not the position, so a shortcut keeps its colour if the
    // order ever changes. Written out rather than composed: Tailwind reads these at build time and a
    // `text-${hue}-500` would be purged.
    tone: 'text-sky-500 hover:text-sky-400 hover:bg-sky-500/10',
    /**
     * Kept on a phone - §9.427, the owner: *"Make sure in the mobile app skratka "archív" on the top
     * bar make it visible, now it is now"*.
     *
     * **This does not reverse §9.11, it applies its own reasoning.** That entry made the shortcuts
     * `hidden md:flex` because *"three more icons would push the switchers off"* - a claim about
     * three, and the owner is asking for one. The other two stay desktop-only, so the bar gains a
     * single 32-pixel control and the argument still holds.
     *
     * The archive is the one they reach for on a phone, which is why it is this one and not a
     * judgement about which is most useful.
     */
    onMobile: true,
    guard: { permission: 'lists' },
  },
  {
    path: '/beverage-stock',
    label: tt('Nápojový sklad'),
    icon: Wine,
    tone: 'text-violet-500 hover:text-violet-400 hover:bg-violet-500/10',
    guard: { permission: 'beverage_stock', franchiseModule: 'stock.beverage' },
  },
  {
    path: '/fiskal-closures',
    label: tt('Pokladňa - uzávierky'),
    icon: Receipt,
    tone: 'text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10',
    guard: { permission: 'fiskal_closures_view', franchiseModule: 'fiskal' },
  },
] as const

/**
 * The sidebar's catalogue of which screen each right opens, lent to the screens below it - FRONTEND.md §9.752's *Na
 * obrazovke* on the roles editor. Built once from the same two lists the sidebar draws.
 */
const MENU_PREVIEW = menuPreviewOf([...NAV_SECTIONS, ...AGOS_NAV_SECTIONS])

export function AppLayout({ children }: { children: ReactNode }) {
  const access = useAccessContext()
  /* §9.688 - the channels that run their own boards. The same cached `GET /sales-channels` the access context
     above already reads, so this costs no request of its own. */
  const { data: channelData } = useGetSalesChannels()
  const boardChannels = useMemo(() => filterChannelsOf(channelData), [channelData])
  const { user, logout, roleName } = useAuth()
  const { restaurantName: designedName, logoUrl } = useTheme()
  /* §9.700 - the name this franchise typed in *Dizajn*, else what the platform calls it. The theme's default
     was *PizzaMajstro*, so every franchise that never typed one wore the owner's brand at the top of its own
     sidebar. */
  const { data: branding } = useGetCentralBranding()
  const restaurantName = restaurantNameOf(designedName, branding)
  const isMobile = useIsMobile()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [, navigate] = useLocation()
  /**
   * §9.631, FB-944 - the sidebar asks before it takes a screen's unsaved work.
   *
   * **The consumer half of FB-941, without which §9.630 shipped a producer nobody read.** Four screens report
   * unsaved work - the design screen, the lunch menu, the burger box and the pizza configuration - and every
   * one of them arms `beforeunload`, so a closed tab asks and **the click an operator actually makes did not**.
   *
   * **Read at the click rather than subscribed to**, which is why the gate is a function: a layout that
   * re-rendered on the answer would re-render on every keystroke in the page it wraps.
   *
   * `pending` is an action rather than a target, so one dialog covers all four navigating call sites in this
   * file - the sidebar entry, the sign-out and the notification panel's two links.
   */
  const unsavedWork = useUnsavedWorkGate()
  const [pending, setPending] = useState<{ run: () => void } | null>(null)
  /** Runs `leave` unless there is work to lose, in which case the question is asked first. */
  const askFirst = (leave: () => void) => (event: { preventDefault: () => void }) => {
    if (!unsavedWork()) {
      leave()
      return
    }
    event.preventDefault()
    setPending({ run: leave })
  }
  /**
   * The sidebar folded away - §9.368, the owner's *"make left panel that we can hide it, add button
   * next to title of the sidebar"*.
   *
   * **Remembered per browser**, because somebody who wants the room wants it on the next screen too,
   * and a till running all evening should not be re-folded every navigation. `localStorage` is the
   * right store for this and the wrong one for anything shared: it is one person's preference on one
   * device, which is exactly what it holds.
   *
   * **Desktop only.** On mobile the sidebar is already a drawer that closes on every navigation, so
   * a second way to hide it would fight the first.
   */
  const [collapsed, setCollapsed] = useState(() => readCollapsed())
  const hidden = !isMobile && collapsed

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current
      writeCollapsed(next)
      return next
    })
  }

  const sidebarVisible = (!isMobile || drawerOpen) && !hidden

  /**
   * How many orders wait on *Rozvoz - nástenka* to be accepted - §9.674, owner O58.
   *
   * Asked only of somebody the sidebar would show that entry to, so a cook without `dispatch` neither sees a
   * count nor makes the read. The rule and the read are the board's module's (`useUnacceptedCount`), so this
   * number is the board's own first band.
   */
  const maySeeDispatch = navEntryVisible(DISPATCH_PATH, NAV_SECTIONS, ROUTE_GUARDS, access)
  const dispatchBadge = badgeText(useUnacceptedCount(maySeeDispatch))
  const badgeFor = (path: string) => (path === DISPATCH_PATH ? dispatchBadge : '')

  return (
    <div className="min-h-screen bg-background">
      {sidebarVisible && (
        <aside
          data-sidebar
          className={cn(
            'fixed inset-y-0 left-0 z-40 w-64 bg-sidebar border-r border-sidebar-border flex flex-col',
            isMobile && 'shadow-2xl',
          )}
        >
          {/* §9.7.2 - the brand no longer scrolls away: only the nav between these two moves. */}
          <div
            data-sidebar-brand
            className="shrink-0 flex items-center gap-2 px-4 h-14 border-b border-sidebar-border"
          >
            {logoUrl ? (
              <img src={logoUrl} alt={restaurantName} className="h-7 w-auto" />
            ) : (
              <span className="font-display font-bold text-sidebar-foreground truncate">
                {restaurantName}
              </span>
            )}
            {/* §9.368 - beside the title, where the owner asked for it. Desktop only: on mobile the
                drawer's own backdrop and every navigation already close it. */}
            {!isMobile && (
              <button
                type="button"
                data-sidebar-collapse
                aria-label={tt('Skryť bočný panel')}
                title={tt('Skryť bočný panel')}
                onClick={toggleCollapsed}
                className="ml-auto shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/5 transition-colors"
              >
                <PanelLeftClose className="w-4 h-4" />
              </button>
            )}
          </div>

          <nav className="sidebar-scroll flex-1 overflow-y-auto py-2">
            {NAV_SECTIONS.map((section) => (
              <SidebarSection
                key={section.heading}
                // §9.688, owner #24 (O79) - a channel's own Kuchyňa and Otvorené stoly, under the ordinary two.
                section={withChannelBoardsIn(withAgosExtras(section), boardChannels)}
                access={access}
                badgeFor={badgeFor}
                onNavigate={(event, href, active) =>
                  /* The active entry navigates nowhere, so a question there would have no answer behind it -
                     §3.175's own exemption, for the same reason. */
                  active
                    ? setDrawerOpen(false)
                    : askFirst(() => {
                        setDrawerOpen(false)
                        navigate(href)
                      })(event)
                }
              />
            ))}
            {/* §9.30 - the pages legacy has no nav entry for because legacy has no such pages. Four
                screens with backends behind them and nothing linking to any of them, which is how
                *"Príplatky a Zálohy kompletne chýbajú"* was true of a feature that was built. */}
            {AGOS_NAV_SECTIONS.filter(isItsOwnSection).map((section) => (
              <SidebarSection
                key={section.heading}
                section={section}
                access={access}
                onNavigate={(event, href, active) =>
                  /* The active entry navigates nowhere, so a question there would have no answer behind it -
                     §3.175's own exemption, for the same reason. */
                  active
                    ? setDrawerOpen(false)
                    : askFirst(() => {
                        setDrawerOpen(false)
                        navigate(href)
                      })(event)
                }
              />
            ))}
          </nav>

          {/* §9.7.3–4 - the end of a shift, where somebody looks for it. Pinned for the same reason
              the brand is: a sidebar nine sections long scrolls, and signing out should not require
              finding the bottom of it. §9.32's punch sits here for that same reason, and above the
              divider: arriving and leaving is the first and last thing anybody does with this app. */}
          <div className="shrink-0 px-0 pb-1">
            <SidebarAttendance />
          </div>
          <div
            data-sidebar-footer
            className="shrink-0 border-t border-sidebar-border p-2 space-y-1"
          >
            <Link
              href="/profile"
              data-sidebar-profile
              onClick={() => setDrawerOpen(false)}
              className="flex items-center gap-3 px-2 py-2 rounded-lg text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-white/5 transition-colors"
            >
              <span className="w-8 h-8 rounded-full bg-sidebar-primary/20 text-sidebar-primary flex items-center justify-center shrink-0">
                <User className="w-4 h-4" />
              </span>
              <span className="min-w-0 leading-tight">
                <span className="block text-sm font-semibold truncate">{user?.name}</span>
                <span className="block text-[11px] text-sidebar-foreground/50" data-user-role>
                  {/* §9.257 - this printed the key, so the owner read `main_admin` and a cook read
                    * `cook`, on every screen. The mapping already existed twice in the app and not
                    * here; it lives in `shared` now because the shell cannot import a module.
                    * O119, §9.753 - a custom role by its own name, the built-in label otherwise. */}
                  {signedInRoleName(user?.systemRole, roleName)}
                </span>
              </span>
            </Link>
            <button
              type="button"
              data-sidebar-logout
              /* The larger loss: signing out discards the session as well as the draft. */
              onClick={askFirst(() => void logout())}
              className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sidebar-foreground/70 hover:text-destructive hover:bg-destructive/10 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center shrink-0">
                <LogOut className="w-4 h-4" />
              </span>
              <span className="text-sm font-semibold">
                <T>Odhlásiť sa</T>
              </span>
            </button>
          </div>
        </aside>
      )}

      {isMobile && drawerOpen && (
        <div className="fixed inset-0 z-30 bg-black/50" onClick={() => setDrawerOpen(false)} />
      )}

      {/* §9.368 - the way back, in the place the sidebar's own toggle was. Fixed so it is reachable
          from anywhere on a long page, and the only control on screen that can bring the panel back. */}
      {hidden && (
        <button
          type="button"
          data-sidebar-expand
          aria-label={tt('Zobraziť bočný panel')}
          title={tt('Zobraziť bočný panel')}
          onClick={toggleCollapsed}
          className="fixed top-3 left-3 z-40 w-9 h-9 rounded-lg bg-card border border-border shadow-sm flex items-center justify-center text-foreground/70 hover:text-foreground transition-colors"
        >
          <PanelLeftOpen className="w-4 h-4" />
        </button>
      )}

      {/* §9.403 - `data-app-content`, because the print stylesheet has to undo this margin. The
          sidebar is `display: none` when printing and this 16 rem was reserving its space anyway,
          so every printed page in the manager started 256 px from the left edge of the paper. A
          hook rather than a `.ml-64` match in the CSS: the width is a layout decision that will
          change, and `[data-sidebar]` in the same block is already there for the same reason. */}
      <div data-app-content className={cn(!isMobile && !hidden && 'ml-64')}>
        {/* FB-1001, §9.684 - **a 56 px row below the inset rather than a 56 px box.** This was a fixed `h-14`, and
          * `.safe-top` puts the iPhone's inset inside that height, so on a home-screen shortcut about 47 px of the
          * 56 went to the status bar and the controls overflowed the bar. The grid's one row is the inner row: at
          * least 56 px, added below the inset, and the two groups sit in it exactly as `justify-between` put them. */}
        <header className="safe-top sticky top-0 z-20 grid grid-cols-[minmax(0,1fr)_auto] grid-rows-[minmax(3.5rem,auto)] items-center gap-3 px-4 bg-card/95 backdrop-blur border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            {isMobile && (
              <button
                type="button"
                onClick={() => setDrawerOpen((open) => !open)}
                aria-label="Menu"
                className="p-2 -ml-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                {drawerOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* §9.1: switchable in-app, without re-login. */}
            {/* §9.112 - the three-way surface *switcher* was here and stays gone: a per-device
              * preference is a second answer to a question the permission system already answers.
              *
              * §9.162 — what returns is **navigation**, not preference. The owner asked for a way to
              * get from the manager to the driving surface *"with permissions"*, and nothing is remembered.
              *
              * §9.759, FB-1078 - each link asks the guard of the route it opens, so it is offered to exactly
              * whom the router admits: `surfacesFor` offered *Rozvoz* to every administrator while the route
              * refused them, and offered nothing to a cook or waiter marked *Jazdí aj rozvoz*. */}
            {headerSurfacesFor(ROUTES, access).map((option) => {
              const Icon = option.surface === 'driving' ? Truck : Utensils
              const label = option.surface === 'driving' ? tt('Rozvoz') : tt('Nástenka')
              return (
                <Link
                  key={option.surface}
                  href={option.path}
                  data-surface-link={option.surface}
                  title={label}
                  aria-label={label}
                  className="flex items-center justify-center min-h-11 min-w-11 md:min-h-0 md:min-w-0 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Icon className="w-5 h-5 md:w-4 md:h-4" />
                </Link>
              )
            })}
            {/* §9.674, owner O58 - the count of unaccepted orders, here whenever the sidebar entry that carries it is
                not on screen: a phone with the drawer closed, or a desktop with the sidebar folded away (§9.368). */}
            {!sidebarVisible && (
              <UnacceptedOrdersLink
                text={dispatchBadge}
                href={DISPATCH_PATH}
                onClick={askFirst(() => navigate(DISPATCH_PATH))}
              />
            )}
            <FranchiseSwitcher />
            {/* §9.11 - one press away, each behind its own page's guard. */}
            {HEADER_SHORTCUTS.filter((shortcut) => navItemVisible(shortcut, ROUTE_GUARDS, access)).map(
              (shortcut) => {
                const Icon = shortcut.icon
                return (
                  <Link
                    key={shortcut.path}
                    href={shortcut.path}
                    data-shortcut={shortcut.path}
                    title={shortcut.label}
                    aria-label={shortcut.label}
                    className={cn(
                      'p-2 rounded-lg transition-colors',
                      // §9.427 - `flex` for the one the owner asked to keep on a phone, `hidden
                      // md:flex` for the rest. Per-shortcut rather than a special case on the path,
                      // so a second one becomes a field rather than another branch here.
                      'onMobile' in shortcut && shortcut.onMobile ? 'flex' : 'hidden md:flex',
                      shortcut.tone,
                    )}
                  >
                    <Icon className="w-4 h-4" />
                  </Link>
                )
              },
            )}
            <NotificationBell askFirst={askFirst} />
            {/* §9.642, owner O41 - the header's own rule, two comments down, is that it keeps "what you change
                mid-shift rather than at the end of one", and a screen somebody cannot read in the light they
                are standing in is exactly that. Saved per employee, so a cook pressing it does not darken the
                manager's screen - which is the state the owner is complaining about. */}
            <ThemeToggle />
            <LanguageMenu />
            {/* The profile and sign-out moved into the sidebar footer - §9.7.3. The header keeps what
                you change mid-shift rather than at the end of one. */}
          </div>
        </header>

        <MenuPreviewProvider value={MENU_PREVIEW}>
          <main className="p-4 sm:p-6">{children}</main>
        </MenuPreviewProvider>
      </div>

      {/* §9.631, FB-944 - at the layout's root rather than inside the sidebar, which is conditional on a phone:
        * a question asked from inside it would unmount with the panel.
        *
        * `ConfirmDialog` rather than `window.confirm`: it renders on 52 screens in this app, it traps the
        * keyboard (§9.441), and it carries markers an e2e case can select. `danger`, because confirming
        * discards work - the treatment this product gives a deletion. The wording is `TowerShell`'s own, so an
        * operator meets one sentence in both applications. */}
      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={() => {
          const leave = pending?.run
          setPending(null)
          leave?.()
        }}
        title={tt('Odísť bez uloženia?')}
        description={tt(
          'Na tejto stránke máte neuložené zmeny. Ak odídete, stratia sa - uložte ich najprv.',
        )}
        confirmLabel={tt('Odísť a zahodiť')}
        cancelLabel={tt('Zostať na stránke')}
        variant="danger"
      />
    </div>
  )
}

/** Holds the app back until the session is known, so nothing flashes. */
export function AuthGate({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth()
  const [location, navigate] = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!user) {
    navigate('/login', { replace: true })
    return null
  }

  /* §9.786, BACKEND.md §10A.438 F - an external courier's app is his pool, his orders and his earnings, all on
     `/driving`. Every other screen reads endpoints the server refuses him, so he is kept there rather than shown a
     page of refusals; the restaurant's own staff are never moved. */
  const courierTo = courierRedirect(isExternalOf(user), location)
  if (courierTo !== null) {
    navigate(courierTo, { replace: true })
    return null
  }

  return <>{children}</>
}
